import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, Mic } from 'lucide-react';

// Landing-page palette: cream, forest green, terracotta
const FOREST = '#2C4A3B';
const FOREST_DARK = '#1E3329';
const TERRA = '#C47A5E';
const CREAM = '#F9F6F0';
const CHARCOAL = '#1C1917';

export default function LoginPage({ onLogin }) {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      onLogin(response.data.token, response.data.user);
      toast.success('Login successful');
      if (inviteToken) {
        navigate(`/accept-invite/${inviteToken}`);
        return;
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ fontFamily: "'Manrope', sans-serif", background: CREAM }}
    >
      {/* Decorative accent */}
      <div
        className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-[0.06]"
        style={{ background: FOREST }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full opacity-[0.08]"
        style={{ background: TERRA }}
        aria-hidden="true"
      />

      <div className="w-full max-w-md relative">
        <div
          className="rounded-3xl shadow-xl p-10 border"
          style={{ background: '#fff', borderColor: '#E3E8E4' }}
        >
          <div className="flex items-center justify-center mb-8">
            <img
              src="/plexura-logo.png"
              alt="Plexura AI Scribe"
              className="w-20 h-20 object-contain"
              data-testid="login-logo"
            />
          </div>

          <h1
            className="text-3xl font-bold text-center mb-2 tracking-tight"
            style={{ color: CHARCOAL, fontFamily: "'Manrope', sans-serif" }}
          >
            Plexura AI Scribe
          </h1>
          <p
            className="text-center text-sm mb-8 flex items-center justify-center gap-2"
            style={{ color: '#6B8E7B' }}
          >
            <Shield className="w-4 h-4" />
            HIPAA-Compliant Session Recording
          </p>

          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium" style={{ color: CHARCOAL }}>
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="provider@example.com"
                required
                data-testid="email-input"
                className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2"
                style={{
                  borderColor: '#E3E8E4',
                  '--tw-ring-color': FOREST,
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = FOREST; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#E3E8E4'; }}
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium" style={{ color: CHARCOAL }}>
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="password-input"
                className="mt-1.5 h-11 rounded-xl focus:outline-none focus:ring-2"
                style={{ borderColor: '#E3E8E4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = FOREST; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#E3E8E4'; }}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-button"
              className="w-full h-11 rounded-xl text-white font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: loading ? FOREST_DARK : FOREST }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = FOREST_DARK; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = FOREST; }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-sm mt-5">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="font-medium hover:underline"
              style={{ color: '#6B8E7B' }}
            >
              Forgot your password?
            </Link>
          </p>

          <p className="text-center text-sm mt-4" style={{ color: '#6B8E7B' }}>
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-semibold hover:underline"
              style={{ color: TERRA }}
              data-testid="register-link"
            >
              Register here
            </Link>
          </p>
        </div>

        {/* Footer touch */}
        <p
          className="text-center text-xs mt-6"
          style={{ color: '#6B8E7B' }}
        >
          © Plexura AI Scribe · HIPAA-compliant mental health scribing
        </p>
      </div>
    </div>
  );
}
